/**
 * PROTOTYPE. The upstream t3code Diagnostics idiom, ported for variant D: a
 * strip of stat blocks separated by hairlines, a hairline table underneath,
 * and a "checked" label with a small refresh control in the section header.
 */
import { LoaderIcon, RefreshCwIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { StatusDot, formatTimestamp } from "../DiagnosticsPanelPrimitives";
import type { StatusTone } from "../DiagnosticsSettings.types";

const VALUE_TONE: Readonly<Record<StatusTone, string>> = {
  ready: "text-foreground",
  info: "text-foreground",
  muted: "text-muted-foreground",
  warning: "text-warning-foreground",
  error: "text-destructive-foreground",
};

export function StatBlock({
  label,
  value,
  tone = "info",
  mono = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: StatusTone;
  readonly mono?: boolean;
}) {
  return (
    <div className="min-w-0 px-4 py-3 sm:px-5">
      <div className="truncate text-[11px] font-medium tracking-[0.08em] text-muted-foreground/70 uppercase">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 truncate text-lg font-semibold tabular-nums",
          mono ? "font-mono" : null,
          VALUE_TONE[tone],
        )}
      >
        {value}
      </div>
    </div>
  );
}

/** Four blocks on desktop, two by two on a phone, hairlines drawn once. */
export function StatsGrid({ children }: { readonly children: ReactNode }) {
  return (
    <div className="relative grid grid-cols-2 border-y border-border/60 sm:grid-cols-4">
      <span
        className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-border/60"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-border/60 sm:hidden"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute inset-y-0 left-1/4 hidden w-px bg-border/60 sm:block"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute inset-y-0 left-3/4 hidden w-px bg-border/60 sm:block"
        aria-hidden
      />
      {children}
    </div>
  );
}

export function PrototypeTable({
  headers,
  children,
}: {
  readonly headers: ReadonlyArray<string>;
  readonly children: ReactNode;
}) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[520px] text-left text-xs">
        <thead className="border-b border-border/60 text-[11px] tracking-[0.08em] text-muted-foreground/70 uppercase">
          <tr>
            {headers.map((header, index) => (
              <th
                key={header}
                className={cn(
                  "px-4 py-2.5 font-semibold whitespace-nowrap first:sm:pl-5 last:sm:pr-5",
                  index === headers.length - 1 ? "text-right" : null,
                )}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">{children}</tbody>
      </table>
    </div>
  );
}

export function CheckRow({
  name,
  result,
  tone,
  observedAt,
}: {
  readonly name: string;
  readonly result: string;
  readonly tone: StatusTone;
  readonly observedAt: string | null;
}) {
  return (
    <tr>
      <td className="px-4 py-2.5 align-top font-medium text-foreground sm:pl-5">{name}</td>
      <td className="px-4 py-2.5 align-top text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <StatusDot tone={tone} />
          <span className="break-words">{result}</span>
        </span>
      </td>
      <td className="px-4 py-2.5 text-right align-top font-mono text-[11px] whitespace-nowrap text-muted-foreground tabular-nums sm:pr-5">
        {formatTimestamp(observedAt)}
      </td>
    </tr>
  );
}

export function EmptyRows({ label }: { readonly label: string }) {
  return <div className="px-4 py-4 text-xs text-muted-foreground sm:px-5">{label}</div>;
}

export function SectionHeaderAction({
  observedAt,
  refresh,
}: {
  readonly observedAt: string | null;
  readonly refresh?: {
    readonly label: string;
    readonly onRefresh: () => void;
    readonly refreshing: boolean;
    readonly canRefresh: boolean;
  };
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground/60">
        {observedAt ? `Checked ${formatTimestamp(observedAt)}` : "Not checked"}
      </span>
      {refresh ? (
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label={refresh.label}
          disabled={refresh.refreshing || !refresh.canRefresh}
          onClick={refresh.onRefresh}
        >
          {refresh.refreshing ? (
            <LoaderIcon className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCwIcon className="size-3.5" aria-hidden="true" />
          )}
        </Button>
      ) : null}
    </div>
  );
}
